import { useNavigate } from "react-router-dom";
import { useEffect, useState, useCallback, useRef } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-hot-toast";
import formatCurrencyINR from "utils/convertNumbToCurrency";
import NcImage from "shared/NcImage/NcImage";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import { useAppDispatch, useAppSelector } from "store/hooks/hooks";
import {
  selectCartItems,
  removeItemAsync,
  updateCartQuantity,
  selectCartError,
  clearCartAsync,
  selectIsCartLoading,
  selectIsCartDataRemoved,
  resolveCartDataRemoved,
  selectHasInitialFetchStarted,
} from "store/slices/cartSlice";
import { motion, AnimatePresence } from "framer-motion";
import Checkbox from "shared/Checkbox/Checkbox";
import BulkEnquiryDialogue from "shared/BulkEnquiryDialogue/BulkEnquiryDialogue";
import { IBulkEnquiryProduct } from "Interfaces/interface.enquirypayload";
import { IAttributeImage } from "Interfaces/interface.productsapi";
import { Alert } from "shared/Alert/Alert";
import { Alert as AlertNew } from "@/components/UI/alert";
import {
  CheckCircle,
  FileText,
  Lightbulb,
  Package,
  ArrowRight,
  Star,
  ShoppingCart,
  Lock,
  WandSparkles,
  Trash2
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "components/UI/Card";
import NcInputNumber from "shared/NcInputNumber";
import { CartUpdateActionType } from "data/cartActions";
import { Badge } from "components/UI/Badge";
import { AlertDescription } from "components/UI/alert";
import { getUserId } from "services/cookieService";
import { openModal } from "store/slices/authModalSlice";
import infiniteLoader from "../../assets/loaders/InfinityLoader.gif";
import ErrorFallback from "shared/ErrorFallback/ErrorFallback";
import {
  filterImageByAttributes,
  getProductNameWithAttributes,
} from "utils/AttributeHandlers";
import { handleMockUpNavigation } from "utils/authHandlers";
import Tippy from "@tippyjs/react";
import ButtonTertiary from "shared/Button/ButtonTertiary";
import EmptyState from "components/EmptyState/EmptyState";
import { selectUserMockups } from "store/slices/mockupSlice";
import { selectUserCatalogs } from "store/slices/catalogSlice";
import { PlatformServices } from "data/tools.enum";
import { Link } from "react-router-dom";
import { selectUser } from "store/slices/authSlice";
import { restrictGenerationLimit } from "utils/toolsUtilities";
import ActionDialog from "shared/Dialog/ActionDialog";
import {
  selectCartMockupAvailability,
  removeItemsFromCartAvailability,
  clearCartConfigurations,
  selectCartConfigurationLoading,
} from "store/slices/configurationSlice";
import Truncate from "utils/Truncate";
import { titleCase } from "utils/ToTitleCase";
import { environment } from "environments/environment";
import { isKit, ITEM_TYPES } from "utils/itemTypeHelpers";
import KitCartCard from "@/components/cart/KitCartCard";
import { FaTools } from "react-icons/fa";


const CartPage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const cartItems = useAppSelector(selectCartItems);
  const hasInitialFetchStarted = useAppSelector(selectHasInitialFetchStarted);
  const error = useAppSelector(selectCartError);
  const isCartLoading = useAppSelector(selectIsCartLoading);
  const userId = getUserId();
  const generatedMockups = useAppSelector(selectUserMockups);
  const generatedCatalogs = useAppSelector(selectUserCatalogs);
  const [localItems, setLocalItems] = useState(cartItems);
  const [isRemoving, setIsRemoving] = useState<string | null>(null);
  const [quantities, setQuantities] = useState<{ [key: string]: number }>({});
  const [selectedFeature, setSelectedFeature] = useState<string | null>(
    "generate-catalog"
  );
  const [selectedProducts, setSelectedProducts] = useState<Set<string>>(
    new Set()
  );
  const [isDialogueOpen, setIsDialogueOpen] = useState(false);
  const [bulkEnquiryProducts, setBulkEnquiryProducts] = useState<
    IBulkEnquiryProduct[]
  >([]);
  const user = useAppSelector(selectUser);
  const isInternalUser = user?.emailAddress?.endsWith("@360customizer.com");
  const maxProductLimit = Number(environment.GIFTHUB_PRODUCTS_PROCESSING_LIMIT);
  const isDataRemoved = useAppSelector(selectIsCartDataRemoved);
  const [clearingCart, setClearingCart] = useState(false);
  const [isNotifyDialogOpen, setIsNotifyDialogOpen] = useState(false);
  const [isResolvingNotification, setIsResolvingNotification] = useState(false);
  const [hasInitialLoadCompleted, setHasInitialLoadCompleted] = useState(false);
  const mockupAvailability = useAppSelector(selectCartMockupAvailability); const cartconfigLoading = useAppSelector(selectCartConfigurationLoading);
  const isInitialLoading = userId
    ? (isCartLoading || !hasInitialFetchStarted) && !hasInitialLoadCompleted
    : false;
  const hasData = localItems.length > 0;
  const shouldShowEmpty = hasInitialLoadCompleted && !hasData && !error;
  const shouldShowError = error && hasInitialLoadCompleted;

  useEffect(() => {
    if (userId) {
      if (!isCartLoading && hasInitialFetchStarted) {
        setHasInitialLoadCompleted(true);
      }
    } else {
      setHasInitialLoadCompleted(true);
    }
  }, [isCartLoading, hasInitialFetchStarted, userId]);

  useEffect(() => {
    setLocalItems(cartItems);
    const initialQuantities = cartItems.reduce(
      (acc: any, item: any) => ({
        ...acc,
        [`${item.id}-${item.variantId}`]: item.quantity,
      }),
      {}
    );
    setQuantities(initialQuantities);
  }, [cartItems]);

  useEffect(() => {
    if (isDataRemoved) {
      setIsNotifyDialogOpen(true);
    }
  }, [isDataRemoved]);

  const handleRemoveOrder = useCallback(
    async (productId: string, variantId: string) => {
      setIsRemoving(`${productId}-${variantId}`);

      const originalItems = [...localItems];
      const originalSelection = new Set(selectedProducts);

      try {
        // Get itemType from localItems to determine if we need productGroupId
        const item = localItems.find(
          (item) => item.id === productId && item.variantId === variantId
        );
        const isKitItem = item?.itemType === ITEM_TYPES.PRODUCT_GROUP;


        await dispatch(
          removeItemAsync({
            productId,
            variantId,
            productGroupId: isKitItem ? productId : undefined,
          })
        ).unwrap();


        dispatch(
          removeItemsFromCartAvailability([`${productId}-${variantId}`])
        );

        setLocalItems((prev) =>
          prev.filter(
            (item) => !(item.id === productId && item.variantId === variantId)
          )
        );
        setSelectedProducts((prev) => {
          const newSet = new Set(prev);
          newSet.delete(`${productId}-${variantId}`);
          return newSet;
        });
        toast.success("Item removed from collection!", { duration: 3000 });
      } catch (err) {
        setLocalItems(originalItems);
        setSelectedProducts(originalSelection);
        toast.error("Failed to remove item. Please try again.", {
          duration: 3000,
        });
      } finally {
        setIsRemoving(null);
      }
    },
    [dispatch, localItems, selectedProducts]
  );

  const handleClearOrder = useCallback(
    async (actionType: string) => {
      setClearingCart(true);
      try {
        await dispatch(clearCartAsync(actionType)).unwrap();
        setSelectedProducts(new Set());
        toast.success("Collection cleared successfully!", { duration: 3000 });
        setClearingCart(false);
        dispatch(clearCartConfigurations());
      } catch (err) {
        toast.error("Failed to clear collection. Please try again.", {
          duration: 3000,
        });
        setClearingCart(false);
      }
    },
    [dispatch]
  );

  const handleQuantityChange = useCallback(
    async (
      productId: string,
      variantId: string,
      qty: number,
      actionType: string
    ) => {
      if (!Number.isInteger(qty) || qty < 25) {
        toast.error("Quantity must be at least 25.", { duration: 3000 });
        return;
      }
      try {
        const result = await dispatch(
          updateCartQuantity(productId, variantId, qty, actionType)
        );

        if (result.meta.requestStatus === "fulfilled") {
          setQuantities((prev) => ({
            ...prev,
            [`${productId}-${variantId}`]: qty,
          }));
          toast.success("Quantity updated successfully!", { duration: 3000 });
        }
      } catch (err) {
        toast.error("Failed to update quantity. Please try again.", {
          duration: 3000,
        });
        const prevQty =
          cartItems.find(
            (item) => item.id === productId && item.variantId === variantId
          )?.quantity || 25;
        setQuantities((prev) => ({
          ...prev,
          [`${productId}-${variantId}`]: prevQty,
        }));
      }
    },
    [dispatch, cartItems]
  );

  const getDisplayQuantity = (productId: string, variantId: string) => {
    return (
      quantities[`${productId}-${variantId}`] ||
      cartItems.find(
        (item) => item.id === productId && item.variantId === variantId
      )?.quantity ||
      25
    );
  };

  const handleGenerateMockup = (productId: string, variantId: string) => {
    if (
      !restrictGenerationLimit(
        PlatformServices.MOCKUP,
        generatedMockups.length,
        isInternalUser
      )
    )
      return;
    const product = localItems.find(
      (item) => item.id === productId && item.variantId === variantId
    );

    if (product) {
      const attributesSelected = product.variants?.[0].attributes;

      const finalUrl = handleMockUpNavigation(product, attributesSelected);

      if (finalUrl) navigate(finalUrl);
    }
  };

  const handleSelectAll = () => {
    setSelectedProducts((prev) =>
      prev.size === localItems.length
        ? new Set()
        : new Set(localItems.map((item) => `${item.id}-${item.variantId}`))
    );
  };

  const handleProceed = (toolId?: string) => {
    if (!selectedProducts.size) {
      toast.error("Please select at least one product to proceed.", {
        duration: 3000,
      });
      return;
    }

    const featureToUse = toolId || selectedFeature;

    if (!featureToUse) {
      toast.error("Please select a feature to proceed.", { duration: 3000 });
      return;
    }

    const selected = localItems.filter((item) =>
      selectedProducts.has(`${item.id}-${item.variantId}`)
    );

    if (featureToUse === "generate-catalog") {
      if (!isInternalUser && selected.length > maxProductLimit) {
        toast.error(`You can select up to ${maxProductLimit} products for catalog generation. Please deselect some products to continue.`);
        return;
      }

      if (
        !restrictGenerationLimit(
          PlatformServices.CATALOG,
          generatedCatalogs.length,
          isInternalUser
        )
      )
        return;

      const sanitizedProducts = selected
        .map((p) => {
          const isKitItem = p.itemType === ITEM_TYPES.PRODUCT_GROUP;

          // Normalize images: group all strings into one object, preserve existing objects
          const stringImages = (p.images || []).filter(
            (img: any) => typeof img === "string"
          );
          const objectImages = (p.images || []).filter(
            (img: any): img is IAttributeImage => typeof img !== "string"
          );

          const normalizedImages: IAttributeImage[] = [...objectImages];
          if (stringImages.length > 0) {
            normalizedImages.push({
              images: stringImages,
              attributeValues: {},
            } as unknown as IAttributeImage);
          }

          // Normalize attributes: prioritize variant attributes
          const variantAttributes =
            p.variants?.[0]?.attributes ||
            (Array.isArray(p.attributes) ? {} : p.attributes || {});

          const matchingImage = filterImageByAttributes(
            { ...p, images: normalizedImages },
            variantAttributes
          );

          return {
            id: p.id,
            name: p.name,
            description: p.description || "",
            price: isKitItem
              ? p.price
              : typeof p.variants?.[0]?.price === "string"
                ? parseFloat(p.variants[0].price)
                : p.variants?.[0]?.price || 0,
            images: matchingImage
              ? matchingImage.images
              : normalizedImages[0]?.images || [],
            categoryId: p.categoryId || "",
            brandId: p.brandId || "",
            brand: titleCase(typeof p.brand === "string" ? p.brand : p.brand || ""),
            sku:
              p.variants?.[0]?.sku ||
              (isKitItem ? `KIT-${p.id.slice(0, 8)}` : ""),
            attributes: variantAttributes,
            itemType: p.itemType,
            product_group_items: p.product_group_items || [],
            productsCount: (p.product_group_items || []).length,
          };
        })
        .reverse();

      setSelectedProducts(new Set());
      setSelectedFeature(null);

      navigate("/catalog-generation-page", {
        state: { products: sanitizedProducts },
      });
    } else if (featureToUse === "bulk-enquiry") {
      const transformed = selected.map((item) => ({
        id: item.id,
        productId: item.id,
        variantId: item.variantId,
        qty: getDisplayQuantity(item.id, item.variantId),
        price: item.price || 0,
        name: item.name || "Unknown",
        image: typeof item.images[0] === 'string'
          ? item.images[0]
          : (item.images[0] as IAttributeImage)?.images?.[0] || "",
        images: item.images as any,
        category: item.category || "",
        brand: item.brand || "",
        variants: item.variants,
        itemType: item.itemType,
        product_group_items: item.product_group_items,
      }));

      setBulkEnquiryProducts(transformed);
      setIsDialogueOpen(true);
    }
    setSelectedProducts(new Set());
    setSelectedFeature(null);
  };


  const handleOkayClick = async () => {
    setIsResolvingNotification(true);
    await dispatch(resolveCartDataRemoved());
    setIsNotifyDialogOpen(false);
    setIsResolvingNotification(false);
  };

  const closeDialogue = () => setIsDialogueOpen(false);

  const toggleProductSelection = (
    _checked: boolean,
    productId: string,
    variantId: string
  ) => {
    setSelectedProducts((prev) => {
      const newSet = new Set(prev);
      newSet.has(`${productId}-${variantId}`)
        ? newSet.delete(`${productId}-${variantId}`)
        : newSet.add(`${productId}-${variantId}`);
      return newSet;
    });
  };

  const totalValue = localItems.reduce(
    (sum, item) =>
      sum + (item.price || 0) * getDisplayQuantity(item.id, item.variantId),
    0
  );
  const totalItems = localItems.reduce(
    (sum, item) => sum + getDisplayQuantity(item.id, item.variantId),
    0
  );
  const selectedItemsValue = localItems
    .filter((item) => selectedProducts.has(`${item.id}-${item.variantId}`))
    .reduce(
      (sum, item) =>
        sum + (item.price || 0) * getDisplayQuantity(item.id, item.variantId),
      0
    );
  const tools = [
    {
      id: "generate-catalog",
      title: "Custom Catalog",
      description:
        "Professional catalog with pricing and customization options",
      icon: FileText,
      color: "bg-blue-500",
      textColor: "text-blue-600",
      bgColor: "bg-blue-50",
      borderColor: "border-blue-200",
      benefits: ["Professional PDF", "Branded design", "Product specs"],
      recommended: totalValue > 3000,
      href: "/catalog-generation-page",
    },
    {
      id: "bulk-enquiry",
      title: "Bulk Enquiry",
      description: "Detailed pricing and terms for bulk orders",
      icon: Package,
      color: "bg-green-500",
      textColor: "text-green-600",
      bgColor: "bg-green-50",
      borderColor: "border-green-200",
      benefits: ["Volume discounts", "Dedicated support", "Fast turnaround"],
      recommended: totalValue > 8000,
      href: "/tools/bulk-enquiry",
    },
  ];
  const getRecommendedTool = () =>
    tools.find((tool) => tool.recommended) || tools[0];
  const recommendedTool = getRecommendedTool();
  const selectedCount = selectedProducts.size;
  const isCatalogTool = tools.some((tool) => tool.title === "Custom Catalog");
  const isBulkTool = tools.some((tool) => tool.title === "Bulk Enquiry");
  const canProceedWithTool =
    selectedCount > 0 &&
    (isBulkTool ||
      isInternalUser ||
      (isCatalogTool && selectedCount <= maxProductLimit));

  const handleContactSales = () => navigate("/contact-us");

  if (shouldShowError) {
    return <ErrorFallback message={error} />;
  }

  if (isInitialLoading) {
    return (
      <div className="flex justify-center items-center min-h-[calc(100vh-100px)]">
        <img src={infiniteLoader} alt="loader" className="w-[14%]" />
      </div>
    );
  }

  return (
    <>
      {shouldShowEmpty && (
        <EmptyState
          icon={ShoppingCart}
          title="Your Collection is Empty"
          message="Looks like you haven't added any products yet..."
          buttonLabel="Browse Products"
          onButtonClick={() => navigate("/products")}
        />
      )}

      {!shouldShowEmpty && (
        <div className="min-h-screen">
          <Helmet>
            <title>Collection || 360CUSTOMIZER</title>
          </Helmet>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="mb-8 text-center lg:text-left">
              <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center justify-center lg:justify-start gap-2">
                Collection
              </h1>
              <p className="text-gray-600">
                {totalItems.toLocaleString()} quantity •{" "}
                {formatCurrencyINR(totalValue)}
              </p>
            </div>
            <div className="lg:grid lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2">
                <Card>
                  <CardHeader>
                    <div className="flex flex-col md:flex-row gap-8 items-start justify-between w-full">
                      <CardTitle className="flex flex-wrap gap-3 md:gap-4 items-center">
                        <span>Selected Items</span>
                        {selectedProducts.size > 0 ? (
                          <>
                            {selectedProducts.size > 0 && (
                              <Badge className="bg-blue-600">
                                {selectedProducts.size} selected
                              </Badge>
                            )}
                          </>
                        ) : (
                          <div></div>
                        )}
                      </CardTitle>
                      <div className="w-full md:w-fit flex items-center justify-end">
                        <div className="w-full flex items-center justify-end gap-2">
                          <ButtonTertiary
                            sizeClass="sm"
                            borderRadius="md"
                            onClick={handleSelectAll}
                          >
                            {selectedProducts.size === localItems.length
                              ? "Deselect All"
                              : "Select All"}
                          </ButtonTertiary>
                        </div>
                      </div>
                    </div>
                    {selectedProducts.size > 0 && (
                      <Alert className="mt-4 border-blue-200 bg-blue-50">
                        <CheckCircle className="h-4 w-4 text-blue-600" />
                        <AlertDescription className="text-blue-800">
                          <strong>
                            {selectedProducts.size} items selected
                          </strong>{" "}
                          for tool processing. Total value:{" "}
                          <strong>
                            {formatCurrencyINR(selectedItemsValue)}
                          </strong>
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardHeader>
                  <CardContent className="!p-2">
                    <div className="max-h-[36rem] overflow-y-auto">
                      <div className="grid gap-4">
                        <AnimatePresence mode="popLayout">
                          {localItems.map((item, index) => {
                            const itemIsKit = isKit(item);

                            // Render KitCartCard for kits
                            if (itemIsKit) {
                              return (
                                <KitCartCard
                                  key={`${item.id}-${item.variantId}`}
                                  item={item}
                                  index={index}
                                  isSelected={selectedProducts.has(
                                    `${item.id}-${item.variantId}`
                                  )}
                                  onSelect={toggleProductSelection}
                                  onRemove={handleRemoveOrder}
                                  onQuantityChange={handleQuantityChange}
                                  isRemoving={
                                    isRemoving === `${item.id}-${item.variantId}`
                                  }
                                  disabled={clearingCart}
                                  getDisplayQuantity={getDisplayQuantity}
                                />
                              );
                            }

                            // Existing product card rendering
                            const variantAttributes =
                              item.variants?.[0]?.attributes || {};

                            let imageUrl =
                              item.images &&
                                Array.isArray(item.images) &&
                                typeof item.images?.[0] === "string"
                                ? item.images?.[0]
                                : filterImageByAttributes(
                                  item as any,
                                  variantAttributes
                                )?.images?.[0] ||
                                (typeof item.images?.[0] === 'string'
                                  ? item.images?.[0]
                                  : (item.images?.[0] as IAttributeImage)?.images?.[0]);

                            return (
                              <motion.div
                                key={`${item.id}-${item.variantId}`}
                                layout
                                initial={{ opacity: 0, scale: 0.95, y: 20 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, x: -100 }}
                                transition={{
                                  duration: 0.25,
                                  delay: index * 0.05,
                                  type: "spring",
                                  stiffness: 100,
                                  damping: 18,
                                }}
                                className={`flex flex-col sm:flex-row items-center justify-between gap-4 px-4 py-5 border rounded-lg transition-colors w-full ${selectedProducts.has(
                                  `${item.id}-${item.variantId}`
                                )
                                  ? "bg-blue-50 border-blue-200"
                                  : "hover:bg-gray-50 border-slate-200 dark:border-slate-700"
                                  }`}
                              >
                                {/* Checkbox - Mobile: Top Right, Desktop: Left */}
                                <div className="self-end sm:self-center">
                                  <Checkbox
                                    name={`select-${item.id}-${item.variantId}`}
                                    checked={selectedProducts.has(
                                      `${item.id}-${item.variantId}`
                                    )}
                                    onChange={(e) =>
                                      toggleProductSelection(
                                        e,
                                        item.id,
                                        item.variantId
                                      )
                                    }
                                    className={`scale-110 ${(clearingCart ||
                                      isRemoving ===
                                      `${item.id}-${item.variantId}`) &&
                                      "pointer-events-none"
                                      }`}
                                  />
                                </div>

                                {/* Content Row: Image + Details */}
                                <div className="flex flex-row w-full sm:flex-1 gap-4 items-stretch sm:items-center">
                                  {/* Image - Mobile: 50% width, Desktop: Fixed size */}
                                  <Link
                                    to={`/product/${item.id}`}
                                    className={`w-1/2 sm:w-14 sm:h-14 relative aspect-square sm:aspect-auto flex-shrink-0 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700 ${(clearingCart ||
                                      isRemoving ===
                                      `${item.id}-${item.variantId}`) &&
                                      "pointer-events-none"
                                      }`}
                                  >
                                    <NcImage
                                      src={imageUrl}
                                      alt={item.name}
                                      className="w-full h-full object-contain absolute inset-0 sm:static"
                                    />
                                  </Link>

                                  {/* Details - Mobile: 50% width, Desktop: Flex 1 */}
                                  <div className="w-1/2 sm:w-auto sm:flex-1 flex flex-col justify-between sm:justify-center">
                                    <div>
                                      <div className="flex justify-between items-start">
                                        <Link
                                          to={`/product/${item.id}`}
                                          className={`font-semibold text-slate-900 dark:text-white text-base line-clamp-2 sm:line-clamp-1 ${(clearingCart ||
                                            isRemoving ===
                                            `${item.id}-${item.variantId}`) &&
                                            "pointer-events-none"
                                            }`}
                                        >
                                          <Truncate
                                            text={titleCase(item.name)}
                                            maxLength={50}
                                          />
                                        </Link>
                                      </div>
                                      <div className="text-sm mt-0.5">
                                        {getProductNameWithAttributes(item)}
                                      </div>
                                      <div className="flex flex-col sm:flex-row sm:items-center gap-2 mt-2 sm:mt-0">
                                        <p className="text-sm font-medium text-slate-900 dark:text-slate-200 h-fit">
                                          {formatCurrencyINR(item.price || 0)}
                                          <span className="text-xs text-slate-500 font-normal ml-1">
                                            /piece
                                          </span>
                                        </p>
                                      </div>
                                    </div>

                                    {/* Mobile Quantity Selector */}
                                    <div className="block sm:hidden mt-2">
                                      <NcInputNumber
                                        id={`qty-mobile-${item.id}-${item.variantId}`}
                                        productId={item.id}
                                        defaultValue={getDisplayQuantity(
                                          item.id,
                                          item.variantId
                                        )}
                                        disabled={
                                          clearingCart ||
                                          isRemoving ===
                                          `${item.id}-${item.variantId}`
                                        }
                                        min={25}
                                        widthClass="w-full"
                                        size="sm"
                                        onChange={(qty) =>
                                          handleQuantityChange(
                                            item.id,
                                            item.variantId,
                                            qty,
                                            CartUpdateActionType.CUSTOM_QUANTITY
                                          )
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>

                                <div className="flex flex-row w-full sm:w-auto items-center justify-end gap-4 sm:gap-8 mt-2 sm:mt-0 pl-0">
                                  {/* Desktop Quantity Selector */}
                                  <div className="hidden sm:block">
                                    <NcInputNumber
                                      id={`qty-${item.id}-${item.variantId}`}
                                      productId={item.id}
                                      defaultValue={getDisplayQuantity(
                                        item.id,
                                        item.variantId
                                      )}
                                      disabled={
                                        clearingCart ||
                                        isRemoving ===
                                        `${item.id}-${item.variantId}`
                                      }
                                      min={25}
                                      widthClass="w-[130px]"
                                      size="sm"
                                      onChange={(qty) =>
                                        handleQuantityChange(
                                          item.id,
                                          item.variantId,
                                          qty,
                                          CartUpdateActionType.CUSTOM_QUANTITY
                                        )
                                      }
                                    />
                                  </div>

                                  <div className="flex gap-2 items-center justify-end w-full sm:w-auto">
                                    <Tippy
                                      content={
                                        !userId
                                          ? "Please sign in to generate a mockup"
                                          : !mockupAvailability[
                                            `${item.id}-${item.variantId}`
                                          ]
                                            ? "Mockup not available for this product"
                                            : "Generate mockup"
                                      }
                                      disabled={
                                        !!userId &&
                                        mockupAvailability[
                                        `${item.id}-${item.variantId}`
                                        ]
                                      }
                                      placement="top"
                                      theme="gradient"
                                      trigger="mouseenter click focus"
                                    >
                                      <motion.div
                                        whileHover={{ scale: 1.05 }}
                                        whileTap={{ scale: 0.95 }}
                                      >
                                        <ButtonPrimary
                                          onClick={() =>
                                            handleGenerateMockup(
                                              item.id,
                                              item.variantId
                                            )
                                          }
                                          disabled={
                                            !userId ||
                                            !mockupAvailability[
                                            `${item.id}-${item.variantId}`
                                            ] ||
                                            isRemoving ===
                                            `${item.id}-${item.variantId}`
                                          }
                                          className={`!text-sm !py-2 px-3 ${!mockupAvailability[
                                            `${item.id}-${item.variantId}`
                                          ]
                                            ? "opacity-50 cursor-not-allowed border-2 border-gray-400/40"
                                            : ""
                                            }`}
                                          loading={cartconfigLoading}
                                          sizeClass="sm"
                                        >
                                          <WandSparkles className="w-4 h-4" />{" "}
                                          <span className="inline ml-2 ">Mockup</span>
                                        </ButtonPrimary>
                                      </motion.div>
                                    </Tippy>
                                    <ButtonSecondary
                                      sizeClass="sm"
                                      fontSize="xs"
                                      onClick={() =>
                                        handleRemoveOrder(
                                          item.id,
                                          item.variantId
                                        )
                                      }
                                      variant="cancel"
                                      className="!py-2 px-3 !text-sm whitespace-nowrap"
                                      loading={
                                        isRemoving ===
                                        `${item.id}-${item.variantId}`
                                      }
                                      icon={<Trash2 className="w-5 h-5" />}
                                    > <span className="inline ml-2">Remove</span></ButtonSecondary>
                                  </div>
                                </div>
                              </motion.div>
                            );
                          })}
                        </AnimatePresence>
                      </div>
                    </div>
                    <AlertNew className="mt-4 border-blue-200 bg-blue-50 mb-5">
                      <AlertDescription className="text-blue-600">
                        <p className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 flex-shrink-0 text-blue-500" />
                          <span>
                            Listed prices are exclusive of{" "}
                            <strong className="font-semibold">
                              GST, Branding, & Delivery
                            </strong>{" "}
                            costs
                          </span>
                        </p>
                      </AlertDescription>
                    </AlertNew>
                    <div className="bg-blue-50 p-4 rounded-lg">
                      <div className="flex justify-between items-center text-xl font-bold">
                        <span>Total Estimated Value</span>
                        <span className="text-blue-600">
                          {formatCurrencyINR(totalValue)}
                        </span>
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        Final pricing may vary based on customization and volume
                        discounts
                      </p>
                      <div className="mt-4 text-right">
                        <ButtonTertiary
                          variant="cancel"
                          loading={clearingCart}
                          onClick={() =>
                            handleClearOrder(CartUpdateActionType.CLEAR)
                          }
                          disabled={localItems.length === 0 || clearingCart}
                        >
                          {!clearingCart && "Clear Collection"}
                        </ButtonTertiary>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                {!canProceedWithTool ? (
                  <Alert className="my-6 xl:mb-0 border-orange-200 bg-orange-50">
                    <Lightbulb className="h-4 w-4 text-orange-600" />
                    <AlertDescription className="text-orange-800">
                      <strong>Select products to proceed!</strong> Choose the
                      items you want to include in your catalog or bulk quote by
                      checking the boxes above.
                    </AlertDescription>
                  </Alert>
                ) : (
                  <Alert className="mt-6 border-blue-200 bg-blue-50">
                    <CheckCircle className="h-4 w-4 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <strong>Ready for the next step!</strong> You've selected{" "}
                      {selectedProducts.size} products. Choose a tool to
                      continue with your selected items.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
              <div id="cart-tools-section" className="relative bg-transparent flex flex-col gap-5">
                <Card className="relative p-2 sm:p-4 rounded-xl border-1 sm:border-2 sm:border-gray-300 mt-10 sm:mt-0 shadow-none sm:shadow-lg bg-transparent sm:bg-white">
                  {!userId && (
                    <div className="z-20 Pin-4 cursor-pointer">
                      <div className="flex items-center justify-center">
                        <div className="text-center bg-white/30 p-6 rounded-xl max-w-sm w-full">
                          <div className="w-14 h-14 mx-auto mb-4 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <Lock className="h-6 w-6 text-white" />
                          </div>
                          <h3 className="text-xl font-semibold text-gray-900 mb-2">
                            Sign In Required
                          </h3>
                          <p className="text-sm text-gray-700 mb-4">
                            These tools are accessible to registered users only.
                            Please proceed with sign in.
                          </p>
                          <ButtonPrimary
                            onClick={() => dispatch(openModal())}
                            className="w-full py-2 px-4 rounded-md"
                          >
                            Sign In Now
                          </ButtonPrimary>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Tool cards */}
                  <div
                    className={`space-y-6 transition-all duration-300 ${!userId ? "grayscale select-none" : ""
                      }`}
                  >
                    {/* Recommended Tool Card */}
                    <Card
                      className={` ${userId && recommendedTool.borderColor} ${userId && recommendedTool.bgColor
                        } ${!userId ? "cursor-not-allowed" : ""}`}
                    >
                      <CardHeader className="p-6 pb-2">
                        <div className="flex items-center space-x-2 mb-2">
                          <Star
                            className={`h-5 w-5 ${recommendedTool.textColor}`}
                          />
                          <Badge className={`${recommendedTool.color}`}>
                            Recommended
                          </Badge>
                        </div>
                        <CardTitle
                          className={` ${!userId ? "text-black" : recommendedTool.textColor
                            } text-lg`}
                        >
                          Perfect Next Step
                        </CardTitle>
                        <CardDescription
                          className={`${recommendedTool.textColor.replace(
                            "600",
                            "700"
                          )}`}
                        >
                          Based on your {totalItems.toLocaleString()} items
                          worth {formatCurrencyINR(totalValue)}
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="p-6 pt-2">
                        <div className="flex gap-4 items-start">
                          <div
                            className={`w-12 h-12 rounded-lg ${recommendedTool.color} flex items-center justify-center flex-shrink-0`}
                          >
                            <recommendedTool.icon className="h-6 w-6 text-white" />
                          </div>
                          <div>
                            <h3
                              className={`font-semibold ${!userId
                                ? "text-black"
                                : recommendedTool.textColor
                                } mb-2`}
                            >
                              {recommendedTool.title}
                            </h3>
                            <p
                              className={`text-sm ${recommendedTool.textColor.replace(
                                "600",
                                "700"
                              )} mb-4`}
                            >
                              {recommendedTool.description}
                            </p>
                          </div>
                        </div>
                        <ButtonPrimary
                          className={`w-full ${!canProceedWithTool || !userId
                            ? "opacity-50 cursor-not-allowed"
                            : "hover:opacity-90"
                            }`}
                          onClick={() => {
                            if (!userId || !canProceedWithTool) return;
                            handleProceed(recommendedTool.id);
                          }}
                          disabled={
                            !canProceedWithTool ||
                            !userId ||
                            (!isInternalUser && selectedCount > maxProductLimit)
                          }
                        >
                          {!selectedCount ? (
                            <span>Select Products First</span>
                          ) : isCatalogTool &&
                            !isInternalUser &&
                            selectedCount > maxProductLimit ? (
                            <span>Max {maxProductLimit} products allowed</span>
                          ) : (
                            <span className="flex items-center justify-center gap-2">
                              Start Now <ArrowRight className="ml-2 h-4 w-4" />
                            </span>
                          )}
                        </ButtonPrimary>
                      </CardContent>
                    </Card>

                    {/* Tool Selection Card */}
                    <Card className={`${!userId ? "cursor-not-allowed" : ""}`}>
                      <CardHeader className="p-6 pb-2">
                        <CardTitle className="text-lg">
                          Choose Your Tool
                        </CardTitle>
                        <CardDescription>
                          Select the best approach for your{" "}
                          {selectedProducts.size > 0
                            ? `${selectedProducts.size} selected `
                            : ""}
                          products
                        </CardDescription>
                      </CardHeader>
                      <CardContent className="space-y-3 p-6 pt-2">
                        {tools.map((tool) => {
                          const IconComponent = tool.icon;
                          const isSelected = selectedFeature === tool.id;

                          return (
                            <div
                              key={tool.id}
                              className={`p-3 border-2 rounded-lg transition-all ${!userId
                                ? "cursor-not-allowed"
                                : "cursor-pointer hover:border-gray-300 hover:bg-gray-50"
                                } ${isSelected
                                  ? `${tool.borderColor} ${tool.bgColor}`
                                  : "border-gray-200"
                                }`}
                              onClick={() => {
                                if (!userId) return;
                                setSelectedFeature(isSelected ? null : tool.id);
                              }}
                            >
                              <div className="flex items-start space-x-3">
                                <div
                                  className={`w-10 h-10 rounded-lg ${tool.color} flex items-center justify-center flex-shrink-0`}
                                >
                                  <IconComponent className="h-5 w-5 text-white" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-center space-x-2 mb-1">
                                    <h4 className="font-semibold text-sm truncate">
                                      {tool.title}
                                    </h4>
                                  </div>
                                  <p className="text-xs text-gray-600 mb-2 line-clamp-2">
                                    {tool.description}
                                  </p>
                                  {isSelected && (
                                    <div className="space-y-1 mb-3">
                                      {tool.benefits.map((benefit, idx) => (
                                        <div
                                          key={idx}
                                          className="flex items-center space-x-1"
                                        >
                                          <CheckCircle className="h-3 w-3 text-green-500 flex-shrink-0" />
                                          <span className="text-xs text-gray-600">
                                            {benefit}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                              {isSelected && (
                                <div className="mt-3 pt-3 border-t">
                                  <ButtonPrimary
                                    className={`w-full ${!userId || !canProceedWithTool
                                      ? "opacity-50 cursor-not-allowed"
                                      : "hover:opacity-90"
                                      }`}
                                    onClick={() => {
                                      if (!userId || !canProceedWithTool)
                                        return;
                                      handleProceed();
                                    }}
                                    disabled={
                                      !canProceedWithTool ||
                                      !userId ||
                                      (!isInternalUser &&
                                        selectedCount > maxProductLimit &&
                                        tool.title === "Custom Catalog")
                                    }
                                  >
                                    {!selectedCount ? (
                                      <span>Select Products First</span>
                                    ) : tool.title === "Custom Catalog" &&
                                      !isInternalUser &&
                                      selectedCount > maxProductLimit ? (
                                      <span>
                                        Max {maxProductLimit} products allowed
                                      </span>
                                    ) : (
                                      <span className="flex items-center justify-center gap-2">
                                        {tool.title === "Bulk Enquiry"
                                          ? "Place"
                                          : "Start"}{" "}
                                        {tool.title}{" "}
                                        <ArrowRight className="h-4 w-4" />
                                      </span>
                                    )}
                                  </ButtonPrimary>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  </div>
                </Card>
                <Card className="bg-gray-50 border-gray-200">
                  <CardContent className="p-4">
                    <h4 className="font-semibold mb-2 text-gray-900">
                      Need Guidance?
                    </h4>
                    <p className="text-sm text-gray-600 mb-3">
                      Not sure which tool fits your needs? Our corporate gifting
                      experts can help you choose the right approach.
                    </p>
                    <ButtonSecondary
                      sizeClass="sm"
                      className="w-full"
                      onClick={handleContactSales}
                    >
                      Get In Touch
                    </ButtonSecondary>
                  </CardContent>
                </Card>
              </div>
            </div>
            {isDialogueOpen && (
              <AnimatePresence>
                <BulkEnquiryDialogue
                  onClose={closeDialogue}
                  products={bulkEnquiryProducts}
                />
              </AnimatePresence>
            )}
          </div>
        </div>
      )}

      <ActionDialog
        isOpen={isNotifyDialogOpen}
        title="Data Removed"
        message="Some products were removed from your cart because they’re no longer available."
        actionText="Okay"
        onAction={handleOkayClick}
        loading={isResolvingNotification}
      />
    </>
  );
};

export default CartPage;
